import React, { Component, useMemo, useState, useEffect, useCallback } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import Select from "react-select";
import { Draggable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  UncontrolledButtonDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
} from "reactstrap";
import AdrLaborRemarksModalV3 from "./AdrLaborRemarksModalV3";
import AddLaborModal from "../projects/projAdmin/labor/AddLaborModal";
import { ADR_EDIT } from "../../_constants/actionConstants";
import { MAX_COLUMNS } from "./utils/adrUtilsV3";
import AlertModal from "../_common/AlertModal";

class AdrLaborSetRowV3 extends Component {
  state = {
    formData: {
      laborHours: {},
    },
    showAddLaborModal: false,
    showLaborRemarksModal: false,
    showAlert_cannotDeleteTaLabor: false,
  };

  componentDidMount() {
    const { laborSetRow } = this.props;

    const formData = {
      laborHours: _.cloneDeep(laborSetRow.laborHours),
    };

    this.setState({ formData });
  }

  componentDidUpdate(prevProps) {
    const { laborSetRow: currLsr } = this.props;
    const { formData } = this.state;

    // Update laborHours if wp columns change
    const prevLaborHourCols = Object.keys(prevProps.laborSetRow.laborHours);
    const currLaborHourCols = Object.keys(currLsr.laborHours);

    // If col lengths are different, sniff out what's updated and reflect in the copy
    // of laborHours from above
    if (prevLaborHourCols.length !== currLaborHourCols.length) {
      // Create a deep copy of existing this.state.formData.laborHours to be edited
      let laborHours = {};
      for (const wpTabId in formData.laborHours) {
        laborHours[+wpTabId] = { ...formData.laborHours[wpTabId] };
      }
      // Check for any new cols
      currLaborHourCols.forEach((wpTabId) => {
        if (!prevLaborHourCols.includes(wpTabId))
          laborHours[+wpTabId] = { ...currLsr.laborHours[wpTabId] };
      });

      // Check for any deleted cols
      prevLaborHourCols.forEach((wpTabId) => {
        if (!currLaborHourCols.includes(wpTabId)) delete laborHours[+wpTabId];
      });

      this.setState({ formData: { ...formData, laborHours } });
    }

    // Update laborHours. This would be otherwise unnecessary if not for
    // the externally-triggered updates from TentativeAgreements.
    for (const wpTabId in prevProps.laborSetRow.laborHours) {
      // If WP column was deleted, currLsr.laborHours[wpTabId] would not exist.
      if (!(wpTabId in currLsr.laborHours)) continue;
      const regHoursChanged =
        currLsr.laborHours[wpTabId].regHours !==
        prevProps.laborSetRow.laborHours[wpTabId].regHours;
      const otHoursChanged =
        currLsr.laborHours[wpTabId].otHours !==
        prevProps.laborSetRow.laborHours[wpTabId].otHours;
      if (regHoursChanged || otHoursChanged)
        this.setState({
          formData: {
            ...formData,
            laborHours: {
              ...formData.laborHours,
              [wpTabId]: {
                ...formData.laborHours[wpTabId],
                regHours: currLsr.laborHours[wpTabId].regHours,
                otHours: currLsr.laborHours[wpTabId].otHours,
              },
            },
          },
        });
    }
  }

  existsInTa = () => {
    const { tentativeAgreements } = this.props;
    const { laborId, tradeId } = this.state.formData;
    return (tentativeAgreements || []).some(
      (ta) => ta.laborsClient && `${laborId}.${tradeId}` in ta.laborsClient
    );
  };

  toggleLaborRemarksModal = () =>
    this.setState({ showLaborRemarksModal: !this.state.showLaborRemarksModal });

  toggleAddLaborModal = () =>
    this.setState({ showAddLaborModal: !this.state.showAddLaborModal });

  toggleAlert_cannotDeleteTaLabor = () =>
    this.setState({
      showAlert_cannotDeleteTaLabor: !this.state.showAlert_cannotDeleteTaLabor,
    });

  handleChange_laborHours = (wpTabId, isOt) => (e) => {
    const { formData } = this.state;

    const timeType = isOt ? "otHours" : "regHours"; // property name
    this.setState(
      {
        formData: {
          ...formData,
          laborHours: {
            ...formData.laborHours,
            [wpTabId]: { ...formData.laborHours[wpTabId], [timeType]: e.target.value },
          },
        },
      },
      this.props.markAdrDirty
    );
  };

  handleBlur_laborHours = (wpTabId, isOt) => (e) =>
    this.props.updateLaborHours(wpTabId, {
      [isOt ? "otHours" : "regHours"]: e.target.value,
    });

  handleClick_addNew = (e) => {
    e.preventDefault();
    this.toggleAddLaborModal();
  };

  handleClick_remarks = (e) => {
    e.preventDefault();
    this.toggleLaborRemarksModal();
  };

  handleClick_delete = (e) => {
    e.preventDefault();
    const { deleteRow, laborSetRow, updateTradesToDisable, markAdrDirty } = this.props;

    if (this.existsInTa()) {
      // Alert user that any labor referenced in TA cannot be deleted
      this.toggleAlert_cannotDeleteTaLabor();
    } else {
      const tradeToUpdate = {
        [laborSetRow.laborId]: {
          toEnable: laborSetRow.tradeId,
          toDisable: 0,
        },
      };
      updateTradesToDisable(tradeToUpdate);

      deleteRow();
      markAdrDirty();
    }
  };

  addLaborCallback = (laborData) => {
    const { addLabor, tradesToDisable, markAdrDirty } = this.props;
    // Check if there is conflict with existing tradeId
    const tradeExists =
      tradesToDisable[laborData.laborId] &&
      tradesToDisable[laborData.laborId].includes(laborData.defaultTradeTradeId);
    const fields = {
      contractorId: laborData.contractorId,
      laborId: laborData.laborId,
      tradeId: tradeExists ? 0 : laborData.defaultTradeTradeId,
    };
    addLabor(fields);
    markAdrDirty && markAdrDirty();
  };

  renderLaborHours = () => {
    const {
      laborSetRow,
      rowKey,
      wpSortOrder,
      workPerformedClient: wpc,
      status,
    } = this.props;
    const { formData } = this.state;

    let lhInputs = [];
    let colIndex = 0;

    wpSortOrder &&
      wpSortOrder.forEach((wpSo) => {
        const lh = laborSetRow.laborHours[wpSo.wpTabId] || null;
        const isIdle = wpc[wpSo.wpTabId] ? wpc[wpSo.wpTabId].isIdle : false;

        lhInputs.push(
          <div
            key={`laborHoursKey_${rowKey}_${colIndex}`}
            className={`labor-equip-padding ${
              isIdle ? "td-labor-hours table-warning" : "td-labor-hours"
            }`}
          >
            <input
              className="form-control px-1"
              type="number"
              id={`laborHoursId_${rowKey}_${colIndex}`}
              name={`laborHours_${rowKey}_${colIndex}`}
              value={
                formData.laborHours[wpSo.wpTabId] &&
                formData.laborHours[wpSo.wpTabId].regHours !== null
                  ? formData.laborHours[wpSo.wpTabId].regHours
                  : ""
              }
              onChange={this.handleChange_laborHours(wpSo.wpTabId, false)}
              onBlur={this.handleBlur_laborHours(wpSo.wpTabId, false)}
              autoComplete="off"
              disabled={
                !lh ||
                !laborSetRow.laborId ||
                !laborSetRow.tradeId ||
                isIdle ||
                status.isReadOnly
              }
            />
          </div>
        );
        colIndex++;
      });

    if (colIndex < MAX_COLUMNS) {
      for (let remCol = colIndex; remCol < MAX_COLUMNS; remCol++) {
        lhInputs.push(
          <div
            key={`laborHoursKey_${rowKey}_${remCol}`}
            className="labor-equip-padding td-labor-hours"
          >
            <input
              className="form-control px-1"
              type="number"
              id={`laborHoursId_${rowKey}_${remCol}`}
              name={`laborHours_${rowKey}_${remCol}`}
              value={""}
              autoComplete="off"
              disabled={true}
            />
          </div>
        );
      }
    }

    return lhInputs;
  };

  renderLaborOvertimeHours = () => {
    const {
      laborSetRow,
      rowKey,
      wpSortOrder,
      workPerformedClient: wpc,
      status,
    } = this.props;
    const { formData } = this.state;

    let lhOtInputs = [];
    let colIndex = 0;

    wpSortOrder &&
      wpSortOrder.forEach((wpSo) => {
        const lh = laborSetRow.laborHours[wpSo.wpTabId] || null;
        const isIdle = wpc[wpSo.wpTabId] ? wpc[wpSo.wpTabId].isIdle : false;

        lhOtInputs.push(
          <div
            key={`laborOtHoursKey_${rowKey}_${colIndex}`}
            className={`labor-equip-padding ${
              isIdle ? "td-labor-hours table-warning" : "td-labor-hours"
            }`}
          >
            <input
              className="form-control px-1"
              type="number"
              id={`laborOtHoursId_${rowKey}_${colIndex}`}
              name={`laborOtHours_${rowKey}_${colIndex}`}
              disabled={
                isIdle ||
                status.isReadOnly ||
                !lh ||
                !laborSetRow.laborId ||
                !laborSetRow.tradeId
              }
              value={
                formData.laborHours[wpSo.wpTabId] &&
                formData.laborHours[wpSo.wpTabId].otHours !== null
                  ? formData.laborHours[wpSo.wpTabId].otHours
                  : ""
              }
              onChange={this.handleChange_laborHours(wpSo.wpTabId, true)}
              onBlur={this.handleBlur_laborHours(wpSo.wpTabId, true)}
              autoComplete="off"
            />
          </div>
        );
        colIndex++;
      });

    if (colIndex < MAX_COLUMNS) {
      for (let remCol = colIndex; remCol < MAX_COLUMNS; remCol++) {
        lhOtInputs.push(
          <div
            key={`laborOtHoursKey_${rowKey}_${remCol}`}
            className="labor-equip-padding td-labor-hours"
          >
            <input
              className="form-control px-1"
              type="number"
              id={`laborOtHoursId_${rowKey}_${remCol}`}
              name={`laborOtHours_${rowKey}_${remCol}`}
              value={""}
              autoComplete="off"
              disabled={true}
            />
          </div>
        );
      }
    }

    return lhOtInputs;
  };

  renderLaborOvertimeRow = () => {
    return (
      <div className="d-flex flex-row flex-wrap lsr-parent">
        <div className="labor-equip-padding td-labor-draghandle"></div>
        <div className="labor-equip-padding td-labor-contractor"></div>
        <div className="labor-equip-padding td-labor-name"></div>
        <div className="labor-equip-padding td-labor-trade"></div>
        <div className="labor-equip-padding td-labor-add"></div>
        <div className="labor-equip-padding td-labor-time-type">
          <span className="badge badge-warning px-1 my-auto">OT</span>
        </div>
        {this.renderLaborOvertimeHours()}
      </div>
    );
  };

  render() {
    const {
      rowKey,
      rowIndex,
      projectId,
      tabId,
      contractorOptions,
      laborOptions,
      tradeOptions,
      tradesToDisable,
      laborSetRow,
      status,
      fetching,
      markAdrDirty,
      toggleOvertimeRow,
      updateTradesToDisable,
    } = this.props;
    const { showLaborRemarksModal, showAddLaborModal, showAlert_cannotDeleteTaLabor } =
      this.state;

    return (
      <Draggable draggableId={rowKey} index={rowIndex}>
        {(provided) => (
          <div {...provided.draggableProps} ref={provided.innerRef}>
            <div className="d-flex flex-row flex-wrap lsr-parent">
              <div className="labor-equip-padding td-labor-draghandle">
                <button
                  type="button"
                  id={`laborDragHandleId_${rowKey}`}
                  className="btn btn-light px-0"
                  style={{ cursor: "move" }}
                  disabled={status.isReadOnly}
                  {...provided.dragHandleProps}
                >
                  <FontAwesomeIcon icon="ellipsis-v" className="fa-fw" />
                </button>
              </div>
              {status.isReadOnly && !fetching ? (
                <AdrLaborSetRowV3_ContractorInput
                  rowKey={rowKey}
                  projectId={projectId}
                  contractorId={laborSetRow.contractorId}
                  laborId={laborSetRow.laborId}
                />
              ) : (
                <AdrLaborSetRowV3_ContractorSelect
                  tabId={tabId}
                  projectId={projectId}
                  rowKey={rowKey}
                  contractorOptions={contractorOptions}
                  contractorId={laborSetRow.contractorId}
                  laborId={laborSetRow.laborId}
                  tradeId={laborSetRow.tradeId}
                  updateTradesToDisable={updateTradesToDisable}
                  isDisabled={status.isReadOnly}
                  markAdrDirty={markAdrDirty}
                />
              )}
              {status.isReadOnly && !fetching ? (
                <AdrLaborSetRowV3_LaborInput
                  projectId={projectId}
                  rowKey={rowKey}
                  laborId={laborSetRow.laborId}
                />
              ) : (
                <AdrLaborSetRowV3_LaborSelect
                  projectId={projectId}
                  tabId={tabId}
                  rowKey={rowKey}
                  laborId={laborSetRow.laborId}
                  laborOptions={laborOptions}
                  contractorId={laborSetRow.contractorId}
                  tradeId={laborSetRow.tradeId}
                  tradesToDisable={tradesToDisable}
                  updateTradesToDisable={updateTradesToDisable}
                  tradeOptions={tradeOptions}
                  isDisabled={status.isReadOnly}
                  markAdrDirty={markAdrDirty}
                />
              )}
              {status.isReadOnly && !fetching ? (
                <AdrLaborSetRowV3_TradeInput tradeId={laborSetRow.tradeId} />
              ) : (
                <AdrLaborSetRowV3_TradeSelect
                  tabId={tabId}
                  rowKey={rowKey}
                  tradeId={laborSetRow.tradeId}
                  tradeOptions={tradeOptions}
                  tradesToDisable={tradesToDisable}
                  updateTradesToDisable={updateTradesToDisable}
                  laborId={laborSetRow.laborId}
                  isDisabled={status.isReadOnly || !laborSetRow.laborId}
                  markAdrDirty={markAdrDirty}
                />
              )}
              <div className="labor-equip-padding td-labor-add">
                <button
                  type="button"
                  name={`laborAdd_${rowKey}`}
                  id={`laborAddId_${rowKey}`}
                  className="btn btn-outline-secondary"
                  onClick={this.handleClick_addNew}
                  disabled={status.isReadOnly}
                >
                  <FontAwesomeIcon icon="user-plus" className="fa-fw" />
                </button>
              </div>
              <div className="labor-equip-padding td-labor-time-type">
                <span className="badge badge-info px-1 my-auto">RT</span>
              </div>
              {this.renderLaborHours()}
              <div className="labor-equip-padding td-labor-dropdown">
                <UncontrolledButtonDropdown
                  color="secondary"
                  disabled={status.isReadOnly}
                  className="float-right"
                >
                  <DropdownToggle caret>
                    <FontAwesomeIcon icon="ellipsis-h" />
                  </DropdownToggle>
                  <DropdownMenu container="body" right>
                    <DropdownItem header>For This Laborer...</DropdownItem>
                    {!laborSetRow.showOvertimeRow ? (
                      <DropdownItem onClick={toggleOvertimeRow}>
                        Show Overtime Row
                      </DropdownItem>
                    ) : (
                      <DropdownItem onClick={toggleOvertimeRow}>
                        Hide Overtime Row
                      </DropdownItem>
                    )}
                    <DropdownItem
                      onClick={this.handleClick_remarks}
                      disabled={!laborSetRow.laborId}
                    >
                      Remarks...
                    </DropdownItem>
                    <DropdownItem disabled>Add Employee Interview...</DropdownItem>
                    <DropdownItem divider />
                    <DropdownItem disabled>View Laborer Profile...</DropdownItem>
                  </DropdownMenu>
                </UncontrolledButtonDropdown>
              </div>
              <div className="labor-equip-padding td-labor-delete">
                <button
                  type="button"
                  id={`deleteLaborWpr_${rowKey}`}
                  onClick={this.handleClick_delete}
                  className="btn btn-danger"
                  disabled={status.isReadOnly}
                >
                  <FontAwesomeIcon icon="times" className="fa-fw px-0" />
                </button>
              </div>
              {showLaborRemarksModal && (
                <AdrLaborRemarksModalV3
                  projectId={projectId}
                  tabId={tabId}
                  rowKey={rowKey}
                  laborId={laborSetRow.laborId}
                  tradeId={laborSetRow.tradeId}
                  remarks={laborSetRow.remarks.remarks}
                  showModal={showLaborRemarksModal}
                  toggleModal={this.toggleLaborRemarksModal}
                  markAdrDirty={markAdrDirty}
                />
              )}
              {showAddLaborModal && (
                <AddLaborModal
                  projectId={projectId}
                  contractorId={laborSetRow.contractorId || 0}
                  dispatchCallback={this.addLaborCallback}
                  showAddLaborModal={showAddLaborModal}
                  toggleAddLaborModal={this.toggleAddLaborModal}
                />
              )}
              {showAlert_cannotDeleteTaLabor && (
                <AlertModal
                  alertKey={"lsrCannotDeleteTaLabor"}
                  alertTitle={"Unable to Delete Labor"}
                  alertTitleClass="bg-warning"
                  alertTitleIcon="exclamation-triangle"
                  alertText="This labor entry is referenced in at least 1 TA(s) for this daily report. To delete, you must first remove this labor entry from any TA that contains this labor entry."
                  acceptCaption={"OK"}
                  acceptClass={"danger"}
                  provideReject={false}
                  handleAlertAction={this.toggleAlert_cannotDeleteTaLabor}
                  showAlertModal={showAlert_cannotDeleteTaLabor}
                  toggleAlertModal={this.toggleAlert_cannotDeleteTaLabor}
                />
              )}
            </div>
            {laborSetRow.showOvertimeRow && this.renderLaborOvertimeRow()}
          </div>
        )}
      </Draggable>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateLaborHours: (wpTabId, fields) =>
    dispatch({
      type: ADR_EDIT.LABORSET.HOURS.UPDATE,
      tabId: ownProps.tabId,
      rowKey: ownProps.rowKey,
      wpTabId,
      fields,
    }),
  deleteRow: () =>
    dispatch({
      type: ADR_EDIT.LABORSET.DELETE,
      tabId: ownProps.tabId,
      rowKey: ownProps.rowKey,
    }),
  toggleOvertimeRow: () =>
    dispatch({
      type: ADR_EDIT.LABORSET.UPDATE,
      tabId: ownProps.tabId,
      rowKey: ownProps.rowKey,
      fields: { showOvertimeRow: !ownProps.laborSetRow.showOvertimeRow },
    }),
  addLabor: (fields) => {
    dispatch({
      type: ADR_EDIT.LABORSET.UPDATE,
      tabId: ownProps.tabId,
      rowKey: ownProps.rowKey,
      fields,
    });
  },
});

const mapStateToProps = (state, ownProps) => ({
  workPerformedClient: state.adr.openAdrs[ownProps.tabId]
    ? state.adr.openAdrs[ownProps.tabId].workPerformedClient || null
    : null,
  tentativeAgreements: state.adr.openAdrs[ownProps.tabId]
    ? state.adr.openAdrs[ownProps.tabId].tentativeAgreements || null
    : null,
});

export default connect(mapStateToProps, mapDispatchToProps)(AdrLaborSetRowV3);

function AdrLaborSetRowV3_ContractorInput(props) {
  const { rowKey, projectId, contractorId, laborId } = props;

  // If labor is selected, auto-select the contractor
  const projLaborList = useSelector(
    (state) => state.labors.projLaborLists[projectId] || null
  );

  const projContractorList = useSelector(
    (state) => state.contractors.projContractorLists[projectId] || null
  );

  const ctrLabel = useMemo(() => {
    if (laborId) {
      const labor = (projLaborList || []).find((l) => l.laborId === laborId) || null;
      const ctr = labor
        ? (projContractorList || []).find(
            (pc) => pc.contractorId === labor.contractorId
          ) || null
        : null;

      if (!ctr) return "(N/A)";

      const labelSuffix = ctr.isPrime ? "(P)" : ctr.isSupplier ? "(Supplier)" : "";
      return `${ctr.name || "(N/A)"} ${labelSuffix}`;
    } else {
      const ctr = (projContractorList || []).find(
        (pc) => pc.contractorId === contractorId
      );
      if (!ctr) return "(N/A)";

      const labelSuffix = ctr.isPrime ? "(P)" : ctr.isSupplier ? "(Supplier)" : "";
      return `${ctr.name || "(N/A)"} ${labelSuffix}`;
    }
  }, [projContractorList, projLaborList]);

  return (
    <div className="labor-equip-padding td-equip-contractor">
      <input
        name="contractorId"
        id={`equipContractorSelectId_${rowKey}`}
        value={ctrLabel}
        onChange={() => {}}
        className="form-control"
        disabled={true}
      />
    </div>
  );
}

function AdrLaborSetRowV3_ContractorSelect(props) {
  const {
    tabId,
    projectId,
    rowKey,
    contractorOptions,
    contractorId: selectedContractorId,
    laborId: selectedLaborId,
    tradeId: selectedTradeId,
    updateTradesToDisable,
    isDisabled,
    markAdrDirty,
  } = props;

  const projLaborList = useSelector(
    (state) => state.labors.projLaborLists[projectId] || null
  );

  const dispatch = useDispatch();

  const [selectedContractor, setSelectedContractor] = useState(
    (contractorOptions || []).find((c) => c.value === selectedContractorId) || null
  );

  useEffect(() => {
    // If labor is selected, auto-select the contractor
    if (selectedLaborId) {
      const labor =
        (projLaborList || []).find((l) => l.laborId === selectedLaborId) || null;
      setSelectedContractor(
        labor
          ? (contractorOptions || []).find((c) => c.value === labor.contractorId)
          : null
      );
    } else {
      setSelectedContractor(
        selectedContractorId
          ? (contractorOptions || []).find((c) => c.value === selectedContractorId) ||
              null
          : null
      );
    }
  }, [contractorOptions, projLaborList, selectedContractorId, selectedLaborId]);

  // Update redux store, which then updates selectedContractorId props and triggers useEffect() that
  // calls setSelectedContractor().
  const handleChange = (selected) => {
    if (selected.value === selectedContractorId) return;

    if (selectedLaborId) {
      updateTradesToDisable({
        [selectedLaborId]: { toEnable: selectedTradeId || 0, toDisable: 0 },
      });
    }

    dispatch({
      type: ADR_EDIT.LABORSET.UPDATE,
      tabId,
      rowKey,
      fields: { contractorId: selected ? selected.value : null, laborId: 0, tradeId: 0 },
    });

    markAdrDirty();
  };

  return (
    <div className="labor-equip-padding td-labor-contractor">
      <Select
        name="contractorId"
        id={`laborContractorSelectId_${rowKey}`}
        value={selectedContractor}
        onChange={handleChange}
        options={contractorOptions || []}
        menuPortalTarget={document.querySelector("body")}
        isDisabled={isDisabled}
      />
    </div>
  );
}

function AdrLaborSetRowV3_LaborInput(props) {
  const { projectId, rowKey, laborId } = props;

  const projLaborList = useSelector(
    (state) => state.labors.projLaborLists[projectId] || null
  );
  const tradeList = useSelector((state) => state.feederLists.tradeList || null);

  const laborLabel = useMemo(() => {
    const labor = (projLaborList || []).find((l) => l.laborId === laborId);
    if (!labor) return "(N/A)";

    const defaultTrade = labor.defaultTradeTradeId
      ? (tradeList || []).find((t) => t.tradeId === labor.defaultTradeTradeId)
      : null;
    const defaultTradeTitle = defaultTrade ? defaultTrade.title : "";

    return `${labor.fullName}${defaultTrade ? ` (${defaultTradeTitle})` : ""}`;
  }, [projLaborList]);

  return (
    <div className="labor-equip-padding td-labor-name">
      <input
        name="laborId"
        id={`laborSelectId_${rowKey}`}
        className="form-control"
        value={laborLabel}
        onChange={() => {}}
        disabled={true}
      />
    </div>
  );
}

function AdrLaborSetRowV3_LaborSelect(props) {
  const {
    projectId,
    tabId,
    rowKey,
    laborId: selectedLaborId,
    contractorId: selectedContractorId,
    tradeId: selectedTradeId,
    laborOptions,
    tradesToDisable,
    updateTradesToDisable,
    tradeOptions,
    isDisabled,
    markAdrDirty,
  } = props;

  const dispatch = useDispatch();

  const projLaborList = useSelector(
    (state) => state.labors.projLaborLists[projectId] || null
  );

  /**
   * Attempts to auto-set defaultTrade for the selected labor.
   * @param {number} laborId
   * @returns {?number} defaultTradeId
   */
  const getDefaultTradeForLabor = useCallback(
    (laborId) => {
      // Find the labor's defaultTrade, if one doesn't exist, return null.
      const labor = (projLaborList || []).find((l) => l.laborId === laborId);
      if (!labor || !labor.defaultTradeTradeId) return null;

      return labor.defaultTradeTradeId;
    },
    [projLaborList]
  );

  const [selectedLabor, setSelectedLabor] = useState(
    (laborOptions || []).find((l) => l.value === selectedLaborId) || null
  );

  // When contractor selection changes, filter labors for that contractor
  const filteredLaborOptions = useMemo(
    () =>
      selectedContractorId
        ? (laborOptions || []).filter((lo) => lo.contractorId === selectedContractorId)
        : laborOptions || [],
    [selectedContractorId, laborOptions]
  );

  // When labor selection or labor options change:
  useEffect(() => {
    // If a labor selection is made...
    if (selectedLaborId) {
      // If no trade is selected, or if a different labor from the same contractor
      // is selected, set the selected labor's default trade as its trade. But if
      // the default trade is already selected elsewhere, set null for trade.
      // (Note: handle trade updates here, not in AdrLaborSetRowV3_TradeSelect, since
      // we have access to previous labor selection in the 'selectedLabor' state.)
      if (
        !selectedTradeId ||
        (selectedLabor && selectedLabor.value !== selectedLaborId)
      ) {
        const defaultTradeId = getDefaultTradeForLabor(selectedLaborId);
        const tradeIdToSet = !(tradesToDisable[selectedLaborId] || []).includes(
          defaultTradeId
        )
          ? defaultTradeId || 0
          : 0;

        updateTradesToDisable({
          [selectedLaborId]: { toEnable: 0, toDisable: tradeIdToSet },
        });

        dispatch({
          type: ADR_EDIT.LABORSET.UPDATE,
          tabId,
          rowKey,
          fields: { tradeId: tradeIdToSet },
        });
      }
    }

    setSelectedLabor(
      selectedLaborId
        ? (filteredLaborOptions || []).find((l) => l.value === selectedLaborId) || null
        : null
    );
  }, [filteredLaborOptions, selectedLaborId]);

  const handleChange = (selected) => {
    if (selected.value === selectedLaborId) return;

    const laborId = selected ? selected.value : null;
    const contractorId = selected ? selected.contractorId : null;
    dispatch({
      type: ADR_EDIT.LABORSET.UPDATE,
      tabId,
      rowKey,
      fields: { laborId, contractorId },
    });

    markAdrDirty();
  };

  return (
    <div className="labor-equip-padding td-labor-name">
      <Select
        name="laborId"
        id={`laborSelectId_${rowKey}`}
        value={selectedLabor}
        options={filteredLaborOptions}
        isOptionDisabled={(option) =>
          tradesToDisable[option.value] &&
          tradeOptions.length === tradesToDisable[option.value].length
        }
        onChange={handleChange}
        placeholder="(Select a Labor)"
        menuPortalTarget={document.querySelector("body")}
        isDisabled={isDisabled}
      />
    </div>
  );
}

function AdrLaborSetRowV3_TradeInput(props) {
  const { tradeId, rowKey } = props;

  const tradeList = useSelector((state) => state.feederLists.tradeList || null);
  const trade = useMemo(
    () => (tradeList || []).find((t) => t.tradeId === tradeId) || null,
    [tradeList]
  );
  const tradeTitle = useMemo(() => (trade ? `${trade.title}` : "(N/A)"), [trade]);

  return (
    <div className="labor-equip-padding td-labor-trade">
      <input
        name="tradeId"
        id={`tradeSelectId_${rowKey}`}
        value={tradeTitle}
        className="form-control"
        onChange={() => {}}
        disabled={true}
      />
    </div>
  );
}

/**
 * Trade selector.
 * @param {*} props
 * @returns
 */
function AdrLaborSetRowV3_TradeSelect(props) {
  const {
    tabId,
    rowKey,
    tradeId: selectedTradeId,
    tradeOptions,
    laborId: selectedLaborId,
    tradesToDisable,
    updateTradesToDisable,
    isDisabled,
    markAdrDirty,
  } = props;

  const dispatch = useDispatch();

  useEffect(() => {
    let tradesToUpdate = {};
    // If the row currently has a labor selected, enable the existing tradeId
    if (selectedLaborId && selectedTradeId)
      tradesToUpdate = {
        [selectedLaborId]: { toEnable: 0, toDisable: selectedTradeId },
      };
    updateTradesToDisable(tradesToUpdate);
  }, []);

  const [selectedTrade, setSelectedTrade] = useState(
    tradeOptions.find((t) => t.value === selectedTradeId) || null
  );
  useEffect(() => {
    if (
      selectedLaborId in tradesToDisable &&
      !(tradesToDisable[selectedLaborId] || []).includes(selectedTradeId)
    ) {
      // If newly selected trade is different from current, enable current
      // and disable newly selected
      if (selectedTradeId && selectedTrade && selectedTrade.value !== selectedTradeId) {
        updateTradesToDisable({
          [selectedLaborId]: {
            toEnable: selectedTrade.value,
            toDisable: selectedTradeId,
          },
        });
      }
    }

    setSelectedTrade(
      selectedTradeId
        ? (tradeOptions || []).find((t) => t.value === selectedTradeId) || null
        : null
    );
  }, [selectedTradeId, tradeOptions]);

  const handleChange = (selected) => {
    if (selected.value === selectedTradeId) return;

    dispatch({
      type: ADR_EDIT.LABORSET.UPDATE,
      tabId,
      rowKey,
      fields: { tradeId: selected ? selected.value : null },
    });

    markAdrDirty();
  };

  return (
    <div className="labor-equip-padding td-labor-trade">
      <Select
        name="tradeId"
        id={`tradeSelectId_${rowKey}`}
        value={selectedTrade}
        options={tradeOptions || []}
        isOptionDisabled={(option) =>
          tradesToDisable[selectedLaborId] &&
          tradesToDisable[selectedLaborId].includes(option.value)
        }
        onChange={handleChange}
        placeholder="(Select Trade)"
        menuPortalTarget={document.querySelector("body")}
        isDisabled={isDisabled}
      />
    </div>
  );
}
